import type { PortalCategory } from '../category/portal-category';
import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import type { ProductRecord } from './product-record';

/**
 * One product record as the catalogue answers for it: the code, and the category membership
 * a caller narrows on.
 *
 * The code is the real one. The catalogue stores it masked, and that key form stops at the
 * adapter (.claude/rules/portal-domain.md).
 *
 * `category` is the catalogue's own underscore-joined multi-level path
 * (`Hudvard_Ansiktsvard_Serum`, .claude/rules/data-access.md), or null when the row carries
 * none. Translating it to the frontend taxonomy is the caller's business, not this port's.
 *
 * `page` is the product's page slug, which the scraper needs to refresh the product's
 * observations (`request_sneak_peek_prefetch` enqueues only rows that carry one); a product
 * without one cannot be refreshed. `name` is the catalogue's display name.
 */
export type BrandCatalogProduct = {
  readonly ean: EAN;
  readonly category: string | null;
  readonly name: string | null;
  readonly page: string | null;
};

/**
 * The portal's global reference data. Catalog has no identity and no
 * commands, so this port only reads.
 */
export abstract class CatalogRepository {
  /**
   * The active portal categories, ordered by display name. Reads the whole
   * taxonomy: paging is the adapter's business, not the caller's.
   */
  abstract listCategories(): Promise<readonly PortalCategory[]>;

  /**
   * Every product the catalogue records for a brand, whole: a brand reaches ~28k rows, so
   * the paging is the adapter's business here too.
   *
   * This is the product-record question. "Which SKUs is this brand made of" is the
   * membership question and goes to `SegmentMembershipStore` over `Brand.definition`, whose
   * callers want a join key and nothing else (PRD D-03). This one carries the category alongside the code
   * because the landing expand narrows on it client-side (PRD D-04).
   */
  abstract productsOfBrand(brand: BrandSlug): Promise<readonly BrandCatalogProduct[]>;

  /**
   * The product records for the codes asked for, in no particular order. A code the
   * catalogue does not hold is simply absent from the answer: the caller decides what an
   * unknown member means.
   */
  abstract productRecords(eans: readonly EAN[]): Promise<readonly ProductRecord[]>;
}
